import { useEffect, useState } from "react";
import {
  Loader2,
  Send,
  Users,
  ChevronRight,
  AlertTriangle,
  Mail,
  Clock,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * OutreachTab — admin emails to owners.
 *
 * Flow:
 *   1. Cohort list (with counts) is fetched on mount.
 *   2. Admin picks a cohort -> preview is fetched (first 50 recipients).
 *   3. Admin writes subject + body (HTML, free-form).
 *   4. Hitting "Send" pops a confirm modal showing the final count.
 *   5. On confirm, POST /api/admin/outreach/send. Result is shown.
 *
 * History tab lists previous sends for audit.
 */
export default function OutreachTab({ token }) {
  const [cohorts, setCohorts] = useState([]);
  const [loadingCohorts, setLoadingCohorts] = useState(true);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [replyTo, setReplyTo] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const headers = { "X-Admin-Token": token };

  const loadCohorts = async () => {
    setLoadingCohorts(true);
    try {
      const res = await api.get("/admin/outreach/cohorts", { headers });
      setCohorts(res.data.cohorts || []);
    } catch (err) {
      toast.error("Couldn't load cohorts", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setLoadingCohorts(false);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await api.get("/admin/outreach/history", { headers });
      setHistory(res.data || []);
    } catch (err) {
      console.warn("[outreach] history load failed", err?.message);
    }
  };

  const loadTemplates = async () => {
    try {
      const res = await api.get("/admin/outreach/templates", { headers });
      setTemplates(res.data?.templates || []);
    } catch (err) {
      console.warn("[outreach] templates load failed", err?.message);
    }
  };

  useEffect(() => {
    loadCohorts();
    loadHistory();
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectCohort = async (id) => {
    setSelectedCohort(id);
    setPreview(null);
    setPreviewing(true);
    try {
      const res = await api.get("/admin/outreach/preview", {
        headers,
        params: { cohort: id, limit: 50 },
      });
      setPreview(res.data);
    } catch (err) {
      toast.error("Couldn't preview recipients", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setPreviewing(false);
    }
  };

  const applyTemplate = (templateId) => {
    setSelectedTemplate(templateId);
    if (!templateId) return;
    const t = templates.find((x) => x.id === templateId);
    if (!t) return;
    setSubject(t.subject);
    setBodyHtml(t.body_html);
    // Auto-pick the cohort the template was designed for (admin can still
    // change it). Only switch if we don't yet have a cohort selected, so
    // we don't yank the recipient list out from under the admin mid-edit.
    if (!selectedCohort && t.suggested_cohort) {
      selectCohort(t.suggested_cohort);
    }
    toast.success(`Loaded template: ${t.label}`);
  };

  const handleSendClick = () => {
    if (!selectedCohort) {
      toast.error("Pick a cohort first");
      return;
    }
    if (!subject.trim() || !bodyHtml.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    if (!preview?.total) {
      toast.error("No recipients in this cohort");
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmSend = async () => {
    setSending(true);
    try {
      const res = await api.post(
        "/admin/outreach/send",
        {
          cohort: selectedCohort,
          subject: subject.trim(),
          body_html: bodyHtml,
          reply_to: replyTo.trim() || null,
        },
        { headers }
      );
      setLastResult(res.data);
      toast.success(
        `Sent to ${res.data.sent}/${res.data.recipients_total} owners`,
        { description: res.data.failed ? `${res.data.failed} failed` : "" }
      );
      setConfirmOpen(false);
      // Refresh cohort counts + history
      loadCohorts();
      loadHistory();
    } catch (err) {
      toast.error("Send failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="outreach-tab" className="space-y-6">
      {/* Header / history toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl text-[#2A3B2C] font-medium">
            Owner outreach
          </h2>
          <p className="text-xs text-[#647466] mt-0.5">
            Send a one-off email to a slice of your owner base. No
            unsubscribe — be intentional.
          </p>
        </div>
        <button
          data-testid="outreach-toggle-history"
          onClick={() => setShowHistory((v) => !v)}
          className="text-xs text-[#647466] hover:text-[#D95D39] inline-flex items-center gap-1"
        >
          <Clock className="w-3.5 h-3.5" />
          {showHistory ? "Hide history" : `History (${history.length})`}
        </button>
      </div>

      {/* History panel */}
      {showHistory && (
        <HistoryPanel history={history} />
      )}

      {/* Cohort picker */}
      <section>
        <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#647466] mb-2 block">
          1 · Pick a cohort
        </Label>
        {loadingCohorts ? (
          <Loader2 className="w-4 h-4 animate-spin text-[#D95D39]" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {cohorts.map((c) => {
              const active = c.id === selectedCohort;
              return (
                <button
                  key={c.id}
                  data-testid={`outreach-cohort-${c.id}`}
                  onClick={() => selectCohort(c.id)}
                  className={`text-left rounded-2xl border p-4 transition-all ${
                    active
                      ? "border-[#D95D39] bg-[#FFF3EE] shadow-sm"
                      : "border-[#E5E1D8] bg-white hover:border-[#2A3B2C]"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Users className="w-4 h-4 text-[#647466] shrink-0" />
                      <span className="text-sm font-medium text-[#2A3B2C] truncate">
                        {c.label}
                      </span>
                    </div>
                    <span
                      data-testid={`outreach-cohort-${c.id}-count`}
                      className="font-display text-lg font-semibold text-[#2A3B2C]"
                    >
                      {c.count}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Template picker — load a pre-built starting point */}
      {templates.length > 0 && (
        <section data-testid="outreach-template-picker">
          <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#647466] mb-2 block">
            Optional · Load a template
          </Label>
          <div className="rounded-2xl border border-[#E5E1D8] bg-white p-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#647466] shrink-0" />
            <select
              data-testid="outreach-template-select"
              value={selectedTemplate}
              onChange={(e) => applyTemplate(e.target.value)}
              className="flex-1 h-10 rounded-lg border border-[#E5E1D8] bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D95D39]"
            >
              <option value="">— start from scratch —</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
            {selectedTemplate && (
              <button
                type="button"
                data-testid="outreach-template-clear"
                onClick={() => {
                  setSelectedTemplate("");
                  setSubject("");
                  setBodyHtml("");
                }}
                className="text-xs text-[#8A9A8B] hover:text-[#D95D39] px-2"
              >
                Clear
              </button>
            )}
          </div>
          <p className="text-[11px] text-[#8A9A8B] mt-1">
            Loading a template pre-fills the subject + body and auto-picks
            the matching cohort. You can still edit both before sending.
          </p>
        </section>
      )}

      {/* Preview */}
      {selectedCohort && (
        <section>
          <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#647466] mb-2 block">
            2 · Preview recipients
          </Label>
          {previewing ? (
            <Loader2 className="w-4 h-4 animate-spin text-[#D95D39]" />
          ) : preview ? (
            <div
              data-testid="outreach-preview"
              className="rounded-2xl border border-[#E5E1D8] bg-white p-4 max-h-60 overflow-y-auto"
            >
              <p className="text-xs text-[#647466] mb-2">
                {preview.total} owner{preview.total === 1 ? "" : "s"} matched ·
                showing first {preview.sample.length}
              </p>
              {preview.sample.length === 0 ? (
                <p className="text-sm text-[#8A9A8B] italic">
                  No matching owners.
                </p>
              ) : (
                <ul className="text-xs space-y-1.5">
                  {preview.sample.map((r) => (
                    <li
                      key={r.email}
                      className="flex items-center gap-2 text-[#2A3B2C]"
                    >
                      <Mail className="w-3 h-3 text-[#8A9A8B] shrink-0" />
                      <span className="font-mono truncate">{r.email}</span>
                      <span className="text-[#8A9A8B] truncate">
                        · {r.restaurant_name}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </section>
      )}

      {/* Compose */}
      {selectedCohort && (
        <section className="space-y-3">
          <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#647466] block">
            3 · Compose
          </Label>
          <Input
            data-testid="outreach-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject (shows in the inbox preview)"
            className="h-11 rounded-xl border-[#E5E1D8] focus-visible:ring-[#D95D39]"
          />
          <textarea
            data-testid="outreach-body"
            value={bodyHtml}
            onChange={(e) => setBodyHtml(e.target.value)}
            placeholder="Write your message. Plain text or basic HTML (<p>, <br>, <strong>, <a>) — we'll wrap it in the AlKoncierge email shell."
            className="w-full min-h-[160px] rounded-xl border border-[#E5E1D8] p-3 text-sm font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D95D39]"
          />
          <Input
            data-testid="outreach-reply-to"
            value={replyTo}
            onChange={(e) => setReplyTo(e.target.value)}
            placeholder="Reply-to email (optional — defaults to no-reply)"
            type="email"
            className="h-10 rounded-xl border-[#E5E1D8] focus-visible:ring-[#D95D39]"
          />
        </section>
      )}

      {/* Send */}
      {selectedCohort && (
        <div className="flex items-center gap-3">
          <button
            data-testid="outreach-send-btn"
            onClick={handleSendClick}
            disabled={!preview?.total || sending}
            className="h-11 px-6 rounded-full bg-[#D95D39] hover:bg-[#C54E2C] disabled:opacity-50 text-white text-sm font-medium inline-flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            Send to {preview?.total ?? 0} owner{preview?.total === 1 ? "" : "s"}
          </button>
          {lastResult && (
            <span
              data-testid="outreach-last-result"
              className="text-xs text-[#647466]"
            >
              Last send: {lastResult.sent}/{lastResult.recipients_total} ·{" "}
              {lastResult.failed} failed
            </span>
          )}
        </div>
      )}

      {/* Confirm modal */}
      {confirmOpen && (
        <ConfirmModal
          count={preview?.total ?? 0}
          subject={subject}
          sending={sending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={handleConfirmSend}
        />
      )}
    </div>
  );
}

function HistoryPanel({ history }) {
  if (history.length === 0) {
    return (
      <div
        data-testid="outreach-history-empty"
        className="rounded-2xl border border-dashed border-[#E5E1D8] bg-white p-4 text-xs text-[#8A9A8B] text-center italic"
      >
        No outreach sent yet.
      </div>
    );
  }
  return (
    <div
      data-testid="outreach-history"
      className="rounded-2xl border border-[#E5E1D8] bg-white p-4 max-h-72 overflow-y-auto"
    >
      <ul className="space-y-2">
        {history.map((h) => (
          <li
            key={h.outreach_id}
            className="flex items-center justify-between gap-3 text-xs border-b border-[#E5E1D8] pb-2 last:border-0 last:pb-0"
          >
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[#2A3B2C] truncate">
                {h.subject}
              </div>
              <div className="text-[#8A9A8B] text-[10px] uppercase tracking-wider mt-0.5">
                {h.cohort || "custom"} ·{" "}
                {new Date(h.sent_at).toLocaleString()}
              </div>
            </div>
            <span className="shrink-0 text-[#2A3B2C] font-mono">
              {h.sent}/{h.recipients_total}
              {h.failed > 0 && (
                <span className="text-[#D95D39] ml-1">·{h.failed} fail</span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ConfirmModal({ count, subject, sending, onCancel, onConfirm }) {
  return (
    <div
      data-testid="outreach-confirm-modal"
      className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-6"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-3xl border border-[#E5E1D8] shadow-2xl p-6"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FFF3EE] flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-[#D95D39]" />
          </div>
          <h3 className="font-display text-lg font-medium text-[#2A3B2C]">
            Send this to {count} owner{count === 1 ? "" : "s"}?
          </h3>
        </div>
        <p className="mt-3 text-sm text-[#647466]">
          Once sent, emails can&apos;t be recalled. The subject line will be:
        </p>
        <p className="mt-2 px-3 py-2 rounded-lg bg-[#FDFBF7] border border-[#E5E1D8] text-sm font-medium text-[#2A3B2C]">
          {subject}
        </p>
        <div className="mt-5 flex gap-2 justify-end">
          <button
            data-testid="outreach-confirm-cancel"
            onClick={onCancel}
            disabled={sending}
            className="h-10 px-4 rounded-full bg-white border border-[#E5E1D8] text-[#647466] text-sm font-medium hover:border-[#2A3B2C]"
          >
            Cancel
          </button>
          <button
            data-testid="outreach-confirm-send"
            onClick={onConfirm}
            disabled={sending}
            className="h-10 px-5 rounded-full bg-[#D95D39] hover:bg-[#C54E2C] disabled:opacity-50 text-white text-sm font-medium inline-flex items-center gap-2"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Send className="w-4 h-4" />
                Yes, send
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
